import React, { useRef } from "react";
import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import { ChevronDown, DoorOpen, MapPin, Sparkles } from "lucide-react";
import Starfield from "@/components/Starfield";
import GoldScript from "@/components/GoldScript";
import LiveSeats from "@/components/LiveSeats";
import Countdown from "@/components/Countdown";
import { MaskedLine, Reveal } from "@/components/Reveal";
import { scrollToSection } from "@/lib/scroll";
import { EVENT, PHOTOS } from "@/lib/data";

const Hero = () => {
  const ref = useRef(null);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [4, -4]), { stiffness: 60, damping: 18 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-6, 6]), { stiffness: 60, damping: 18 });

  const onMove = (e) => {
    const r = ref.current?.getBoundingClientRect();
    if (!r) return;
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };

  return (
    <section
      ref={ref}
      onMouseMove={onMove}
      className="relative min-h-screen flex items-center overflow-hidden"
      style={{ background: "radial-gradient(ellipse 80% 60% at 50% -10%, #1a2450 0%, #0B1021 62%)" }}
    >
      <Starfield />

      <div className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8 pt-28 pb-24 grid lg:grid-cols-[1.08fr_0.92fr] gap-12 lg:gap-16 items-center w-full">
        {/* Copy */}
        <div className="min-w-0">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="overflow-hidden border-y border-[#D4AF37]/25 bg-[#0E142B]/70 py-2.5 mb-7 -mx-5 sm:mx-0 sm:rounded-full sm:border"
            data-testid="hero-announcement-bar"
          >
            <div className="flex w-max animate-marquee-fast">
              {[0, 1].map((dup) => (
                <div key={dup} aria-hidden={dup === 1 || undefined} className="flex items-center shrink-0">
                  {["One Night Only", "New Year 2027 Premiere", "Live TV Show Taping", "An Evening of Mediumship", "The Saban Theatre · Beverly Hills", "Thursday · January 14 · 2027"].map((item, i) => (
                    <React.Fragment key={i}>
                      <span className="text-[10px] sm:text-[11px] uppercase tracking-[0.24em] text-[#F3D068] px-6 whitespace-nowrap">
                        {item}
                      </span>
                      <span className="w-1 h-1 rotate-45 bg-[#D4AF37]/70 shrink-0" aria-hidden="true" />
                    </React.Fragment>
                  ))}
                </div>
              ))}
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.3 }}
            data-testid="hero-when-where"
            className="mb-8 rounded-2xl overflow-hidden border border-[#D4AF37]/30 bg-gradient-to-r from-[#D4AF37]/10 via-transparent to-[#D4AF37]/10 backdrop-blur-sm shadow-[0_10px_40px_rgba(212,175,55,0.08)]"
          >
            <div className="grid grid-cols-3 divide-x divide-[#D4AF37]/20">
              <div className="px-2 py-4 sm:px-6 sm:py-5 text-center">
                <p className="text-[9px] sm:text-[10px] uppercase tracking-[0.25em] text-[#8A99B5]">Thursday</p>
                <p className="font-display text-xl sm:text-3xl lg:text-4xl text-gold-gradient leading-none mt-1.5 whitespace-nowrap">
                  Jan 14
                </p>
                <p className="text-[9px] sm:text-[10px] uppercase tracking-[0.25em] text-[#8A99B5] mt-1.5">2027</p>
              </div>
              <div className="px-2 py-4 sm:px-6 sm:py-5 flex flex-col items-center justify-center gap-1.5 text-center">
                <p className="text-[10px] sm:text-sm text-[#FDFBF7] flex items-center gap-1.5 whitespace-nowrap">
                  <DoorOpen size={13} className="text-[#D4AF37] shrink-0" /> Doors 6:00 PM
                </p>
                <p className="text-[10px] sm:text-sm text-[#FDFBF7] flex items-center gap-1.5 whitespace-nowrap">
                  <Sparkles size={13} className="text-[#D4AF37] shrink-0" /> Show 7:00 PM
                </p>
                <p className="text-[9px] text-[#8A99B5]">until 9:30 PM</p>
              </div>
              <div className="px-2 py-4 sm:px-6 sm:py-5 flex flex-col items-center justify-center gap-1.5 text-center">
                <p className="text-[10px] sm:text-sm text-[#F3D068] font-medium leading-snug flex items-center justify-center gap-1.5">
                  <MapPin size={13} className="shrink-0" /> The Saban Theatre
                </p>
                <p className="text-[10px] sm:text-xs text-[#C5CEE0]">Beverly Hills</p>
                <p className="text-[9px] text-[#8A99B5] hidden sm:block">8440 Wilshire Blvd</p>
              </div>
            </div>
          </motion.div>

          <h1
            data-testid="hero-title"
            className="font-display font-light leading-[0.95] text-[16vw] sm:text-7xl lg:text-[5.4rem]"
          >
            <MaskedLine delay={0.25} className="text-[#FDFBF7]">A View</MaskedLine>
            <MaskedLine delay={0.42} className="text-[#FDFBF7]">from the Top</MaskedLine>
            <MaskedLine delay={0.6}>
              <span className="text-gold-gradient italic font-normal">LIVE</span>
            </MaskedLine>
            <MaskedLine delay={0.75} className="text-[0.42em] tracking-[0.18em] text-[#E0A96D]">
              <span className="uppercase font-sans font-light">with Susan Grau</span>
            </MaskedLine>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.95 }}
            className="mt-6 max-w-xl text-[#9CA3AF] font-light leading-relaxed"
          >
            Her celebrated show steps off the soundstage at CBS Television City for one candlelit
            January night at one of Los Angeles' grandest theatres — the LIVE event version,
            taped for its New Year 2027 special premiere, and messages from the other side that
            will carry you into the new year.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.1 }}
            className="mt-8 flex flex-wrap items-center gap-4"
          >
            <button
              data-testid="hero-primary-cta"
              onClick={() => scrollToSection("seating")}
              className="btn-gold rounded-full px-9 py-4 text-sm font-bold tracking-wide"
            >
              Select Seats — from $90
            </button>
            <button
              data-testid="hero-gift-cta"
              onClick={() => scrollToSection("gifting")}
              className="btn-ghost-gold rounded-full px-8 py-4 text-sm font-medium"
            >
              Gift a Christmas Ticket
            </button>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.4 }}
            className="mt-8"
          >
            <p className="text-[11px] uppercase tracking-[0.3em] text-[#8A99B5] mb-3">
              Countdown to showtime
            </p>
            <Countdown />
            <div className="mt-4">
              <LiveSeats />
            </div>
          </motion.div>
        </div>

        {/* Red carpet portrait with celebrity proof */}
        <motion.div
          initial={{ opacity: 0, scale: 0.94 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1.3, delay: 0.6, ease: [0.22, 1, 0.36, 1] }}
          className="relative mx-auto w-[84%] sm:w-[68%] lg:w-full max-w-[440px] pb-10"
          style={{ perspective: 1000 }}
        >
          <motion.div style={{ rotateX: rx, rotateY: ry }} className="relative">
            <div
              className="absolute -inset-10 rounded-full bg-[radial-gradient(circle,rgba(245,158,11,0.2),rgba(124,116,242,0.14)_45%,transparent_72%)] blur-2xl glow-pulse"
              aria-hidden="true"
            />
            <div className="relative rounded-3xl overflow-hidden border border-[#D4AF37]/30 shadow-2xl shadow-black/80 gold-ring">
              <motion.img
                src={PHOTOS.hero}
                alt="Susan Grau on the red carpet at the Beverly Hills Film Festival"
                className="w-full h-auto block"
                initial={{ scale: 1.1 }}
                animate={{ scale: 1 }}
                transition={{ duration: 2.2, delay: 0.6, ease: [0.22, 1, 0.36, 1] }}
              />
            </div>
          </motion.div>

          {/* Her name, written in gold on the night sky */}
          <GoldScript text="Susan Grau" />
        </motion.div>
      </div>

      <motion.button
        onClick={() => scrollToSection("story")}
        className="absolute bottom-7 left-1/2 -translate-x-1/2 text-[#D4AF37] z-10"
        animate={{ y: [0, 10, 0] }}
        transition={{ repeat: Infinity, duration: 2.2 }}
        aria-label="Scroll to explore"
      >
        <ChevronDown size={28} strokeWidth={1.2} />
      </motion.button>
    </section>
  );
};

export default Hero;
